import { alpha, useTheme } from '@mui/material/styles'
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type KeyboardEvent,
} from 'react'
import { FiSend } from 'react-icons/fi'
import { useChat } from '../context/ChatProvider'

const TYPING_IDLE_MS = 2000

const resizeTextarea = (textarea?: HTMLTextAreaElement | null) => {
  if (!textarea) return;
  textarea.style.height = "auto";
  textarea.style.height = `${Math.min(textarea.scrollHeight, 200)}px`;
};

export const MessageInput = () => {
  const theme = useTheme()
  const { sendMessage, beginTyping, endTyping, loading } = useChat()
  const [value, setValue] = useState('')
  const [sending, setSending] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement | null>(null)
  const typingTimerRef = useRef<ReturnType<typeof setTimeout>>()

  useEffect(() => {
    resizeTextarea(textareaRef.current);
  }, []);

  useEffect(() => {
    return () => {
      if (typingTimerRef.current) {
        clearTimeout(typingTimerRef.current);
      }
      endTyping();
    };
  }, [endTyping]);

  const scheduleTypingEnd = useCallback(() => {
    if (typingTimerRef.current) {
      clearTimeout(typingTimerRef.current);
    }
    typingTimerRef.current = setTimeout(() => {
      endTyping();
    }, TYPING_IDLE_MS);
  }, [endTyping]);

  const handleChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    setValue(event.target.value);
    beginTyping();
    resizeTextarea(event.target);
    scheduleTypingEnd();
  };

  const handleSend = useCallback(async () => {
    const trimmed = value.trim();
    if (!trimmed) return;

    setSending(true);
    try {
      await sendMessage(trimmed);
      setValue("");
      resizeTextarea(textareaRef.current);
    } finally {
      setSending(false);
      endTyping();
    }
  }, [endTyping, sendMessage, value]);

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  return (
    <div
      className="flex items-end gap-3 rounded-2xl border bg-slate-50/70 px-4 py-3 shadow-inner"
      style={{ borderColor: alpha(theme.palette.primary.main, 0.15) }}
    >
      <textarea
        ref={textareaRef}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder="Type a message…"
        className="max-h-[200px] min-h-[44px] flex-1 resize-none bg-transparent text-sm text-slate-800 outline-none placeholder:text-slate-400"
        aria-label="Message input"
      />
      <button
        type="button"
        onClick={handleSend}
        disabled={!value.trim() || sending}
        className="flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold text-white shadow-lg transition disabled:cursor-not-allowed disabled:opacity-50"
        style={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
          boxShadow: `0 12px 30px ${alpha(theme.palette.primary.main, 0.25)}`,
        }}
      >
        Send
        <FiSend />
      </button>
    </div>
  );
};


